import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Popover, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { API_LIMIT } from '@/constants/api'
import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Customer } from '@/types'
import { GetAllCustomersRequest } from '@/types/apis/customers/GetAllCustomersRequest'
import { GetAllCustomersResponse } from '@/types/apis/customers/GetAllCustomersResponse'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: GetAllCustomersResponse | undefined
  getCustomersLoading: boolean
  onDelete: (id: Customer['id']) => void
  getCustomers: (request: GetAllCustomersRequest) => void
}

const CustomersTable = ({ dataSource, getCustomersLoading, onDelete, getCustomers }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const goToDetailCustomerPage = (id: Customer['id']) => {
    const path = routes.customerDetail.path.replace(':id', id.toString())
    navigate(path)
  }

  const columns: ColumnsType<Customer> = [
    {
      title: t('components.pages.customers.list.table.companyId'),
      dataIndex: 'code',
      key: 'code',
      render: (record: Customer) => {
        return <CustomRowTable type="center">{record}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.customers.list.table.shortName'),
      dataIndex: 'shortName',
      key: 'shortName',
      render: (shortName: Text) => {
        return <CustomRowTable type="left">{shortName}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.customers.list.table.companyName'),
      dataIndex: 'name',
      key: 'name',
      render: (record: Customer) => {
        return <CustomRowTable type="left">{record}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  columns.push({
    align: 'center' as const,
    render: (text: string, record: Customer) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <Button size="small" onClick={() => goToDetailCustomerPage(record.id)}>
                  {t('components.pages.admins.list.table.detail')}
                </Button>
                <Button size="small" onClick={() => goToEditCustomerPage(record.id)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const goToEditCustomerPage = (id: Customer['id']) => {
    navigate(`${routes.customers.path}/${id}`)
  }

  return (
    <Table
      rowKey="id"
      bordered
      dataSource={dataSource?.items}
      columns={columns}
      loading={getCustomersLoading}
      pagination={{
        total: dataSource?.meta.totalItems,
        current: dataSource?.meta.currentPage,
        pageSize: API_LIMIT,
        onChange: (page: number, pageSize: number) => {
          getCustomers({ page, limit: pageSize })
        },
      }}
      scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
    />
  )
}

CustomersTable.displayName = 'CustomersTable'
export default CustomersTable
